/**
 * Klientsidans filtrering av resultatlistan (pris + mattyp).
 * Ren funktion — reproducerar prototypens `buildDataFilters` + `applyFilters`.
 *
 * Semantik (som prototypen):
 *  - OR inom en grupp (matchar något av de valda priserna/typerna)
 *  - AND mellan grupper (priset OCH mattypen måste passera)
 *  - Tom grupp = ingen begränsning (släpper igenom).
 *
 * `family` och `preferences` ignoreras tills vidare (family-chips renderas inte,
 * preferences är ett fritt fält) — men de får aldrig krascha filtreringen.
 */

import type {
  CuisineTag,
  PriceBucket,
  PriceLevel,
  Restaurant,
  SearchFilters,
} from '@bilmat/shared';

/**
 * Pris → prisbucket, exakt som prototypens trösklar:
 *   1 → budget, 2 → mellan, ≥ 3 → dyrare.
 * Pris 0 och okänt pris hör inte till någon bucket (returnerar null).
 */
export function priceBucket(level: PriceLevel | undefined): PriceBucket | null {
  if (level === 1) return 'budget';
  if (level === 2) return 'mellan';
  if (level !== undefined && level >= 3) return 'dyrare';
  return null;
}

/**
 * Mattyp → regex mot Google-typer + namn + vicinity, ordagrant från prototypen.
 * `cafe`-regexen täcker även Google-typerna `cafe`/`bakery`.
 */
const CUISINE_MATCHERS: Record<CuisineTag, RegExp> = {
  vegetariskt: /vegetar|vegan/,
  asiatiskt:
    /chinese|japan|sushi|thai|asian|korean|vietnam|india|wok|ramen|pho|dim.?sum|mongolian|taiwanese|singapor|hong.?kong|peking|szechuan|cantonese|udon|tempura|teriyaki|bento|izakaya|kina|kinesisk|japansk|sushirestaurang/,
  italienskt:
    /italian|pizza|pasta|trattoria|osteria|ristorante|pizzeria|focaccia|calzone|lasagna|risotto/,
  snabbmat:
    /fast.?food|takeaway|take.?away|meal_takeaway|hamburger|burger|mcdonalds|max burger|subway|kebab|hot.?dog|korvkiosk|gatukök|drive.?in|drive.?through|kiosk/,
  husmanskost:
    /husmanskost|husman|värdshus|wardshus|krog|gastro|nordisk|nordic|smörgås|smorgasbord|köttbullar|raggmunk|swedish.?kitchen|svensk.?mat/,
  cafe: /café|cafe|kafé|kafe|konditori|bageri|bakery|patisserie|fika|bröd|coffeehouse|coffee.?shop|espresso|barista/,
};

/** Reproducerar prototypens textbygge: typer + namn + vicinity, gemener. */
function matchText(restaurant: Restaurant): string {
  const types = restaurant.types.join(' ').toLowerCase();
  const name = restaurant.name.toLowerCase();
  const vicinity = (restaurant.vicinity ?? '').toLowerCase();
  return `${types} ${name} ${vicinity}`;
}

function matchesCuisine(restaurant: Restaurant, cuisine: CuisineTag): boolean {
  return CUISINE_MATCHERS[cuisine].test(matchText(restaurant));
}

/**
 * Filtrera restauranglistan på pris + mattyp. Ren — muterar inget, inga sidoeffekter.
 * Returnerar ny array (bevarar ordningen).
 */
export function applyFilters(
  restaurants: Restaurant[],
  filters: SearchFilters,
): Restaurant[] {
  const { price, cuisine } = filters;
  if (price.length === 0 && cuisine.length === 0) return restaurants;

  return restaurants.filter((restaurant) => {
    const priceOk =
      price.length === 0 ||
      (() => {
        const bucket = priceBucket(restaurant.priceLevel);
        return bucket !== null && price.includes(bucket);
      })();

    const cuisineOk =
      cuisine.length === 0 || cuisine.some((c) => matchesCuisine(restaurant, c));

    return priceOk && cuisineOk;
  });
}
